/**
 * CombatantListContainer - Container for the combatant list with drag-and-drop.
 *
 * Follows ADR-034 Container/Presenter pattern:
 * - Connects to Zustand store via useActions for reorder mutations
 * - Reads image library from useImageLibraryStore for combatant avatars
 * - Derives the imageMap for efficient lookup
 * - Passes all data and callbacks as props to CombatantList presenter
 *
 * The CombatantList presenter renders CombatantListItemContainer for each
 * combatant, since each item has its own set of store-backed callbacks
 * (HP change, initiative change, remove, status effects). This avoids
 * threading many callbacks through CombatantList.
 *
 * @see docs/adr/0034-container-presenter-component-pattern.md
 */

import React, { useMemo } from 'react';
import { useActions } from '../../hooks/useStore';
import { useImageLibraryStore } from '../../state/createImageLibraryStore';
import { useStableCallback } from '../../hooks/hookUtils';
import CombatantList from './CombatantList';
import type { Combatant, Image } from '../../types/types';

interface CombatantListContainerProps {
  combatants: Combatant[];
  isActive: boolean;
  setupMode: boolean;
  turn: number;
}

/**
 * Container component for CombatantList.
 *
 * Owns store access for:
 * - Image library (for combatant avatar URLs)
 * - Actions (for drag-and-drop reorder)
 *
 * Combatant data is received as props from CombatTrackerContainer,
 * since the combat state is already read at the top level.
 */
const CombatantListContainer: React.FC<CombatantListContainerProps> = ({
  combatants,
  isActive,
  setupMode,
  turn,
}) => {
  const imageLibrary = useImageLibraryStore(state => state.images);
  const actions = useActions();

  // Derive imageMap from the image library for efficient avatar lookups.
  const imageMap: Map<number, string> = useMemo(
    () => new Map((imageLibrary ?? []).map((img: Image) => [img.id, img.imageUrl])),
    [imageLibrary]
  );

  // Stable handler for drag-and-drop reordering.
  // Only triggers in setup mode; reorders combatants by their IDs.
  const handleReorder = useStableCallback((reorderedIds: number[]) => {
    actions.updateCombatantOrder(reorderedIds);
  });

  return (
    <CombatantList
      combatants={combatants}
      isActive={isActive}
      setupMode={setupMode}
      turn={turn}
      imageMap={imageMap}
      onReorder={handleReorder}
    />
  );
};

export default CombatantListContainer;
